import { useCallback, useEffect, useState } from 'react';
import { withSkillHotkey } from '@sorrem-client/ui/shared/action-bar';
import { useActionBarTargets, useInputBindingPress } from '@sorrem-client/ui/hooks';

/** How long the loadout stays up after a skill is assigned, outlining the slot it went to. */
const ASSIGNED_CONFIRM_MS = 700;

export interface SkillHotkeyAssignment {
    /** The skill waiting for the player to pick its action slot, or null. */
    readonly assigningSkillId: string | null;
    /** Starts (with a skill id) or cancels (null) picking an action slot for a skill. */
    readonly setAssigningSkillId: (skillId: string | null) => void;
    /** Puts the waiting skill on hotkey `hotkeyIndex`. */
    readonly assign: (hotkeyIndex: number) => void;
    /** The slot a skill just went to, outlined until the loadout hides again; null otherwise. */
    readonly confirmedHotkeyIndex: number | null;
    /** Whether the loadout should show for the assignment: while picking a slot, and briefly after. */
    readonly isLoadoutShown: boolean;
}

/**
 * The Skills tab's "assign to a hotkey" mode: the loadout comes up, the
 * player clicks an action slot (or presses its key), and the skill goes
 * there. Esc cancels, as does leaving the tab or selecting another skill.
 *
 * @param isActive whether the Skills tab is open.
 * @param selectedSkillId the skill whose details are open.
 */
export function useSkillHotkeyAssignment(isActive: boolean, selectedSkillId: string | null): SkillHotkeyAssignment {
    const { targets, setTargets } = useActionBarTargets();
    const [assigningSkillId, setAssigning] = useState<string | null>(null);
    const [confirmedHotkeyIndex, setConfirmedHotkeyIndex] = useState<number | null>(null);

    // Leaving the tab or the skill ends the mode.
    if (assigningSkillId !== null && (!isActive || selectedSkillId !== assigningSkillId)) {
        setAssigning(null);
    }
    if (confirmedHotkeyIndex !== null && !isActive) {
        setConfirmedHotkeyIndex(null);
    }

    useEffect(() => {
        if (confirmedHotkeyIndex === null) return undefined;
        const timer = window.setTimeout(() => setConfirmedHotkeyIndex(null), ASSIGNED_CONFIRM_MS);
        return (): void => window.clearTimeout(timer);
    }, [confirmedHotkeyIndex]);

    const setAssigningSkillId = useCallback((skillId: string | null): void => {
        // The page input ignores keys while a button has focus, and the
        // action-bar key that picks the slot must reach it.
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setConfirmedHotkeyIndex(null);
        setAssigning(skillId);
    }, []);

    const assign = useCallback((hotkeyIndex: number): void => {
        if (assigningSkillId === null) return;
        setTargets(withSkillHotkey(targets, hotkeyIndex, assigningSkillId));
        setAssigning(null);
        setConfirmedHotkeyIndex(hotkeyIndex);
    }, [assigningSkillId, setTargets, targets]);

    useInputBindingPress('open-menu', assigningSkillId === null ? undefined : (): void => setAssigning(null));

    return {
        assigningSkillId,
        setAssigningSkillId,
        assign,
        confirmedHotkeyIndex,
        isLoadoutShown: assigningSkillId !== null || confirmedHotkeyIndex !== null,
    };
}
